import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { orderApi, paymentApi } from '../services/api';
import toast from 'react-hot-toast';

const STATUS_OPTIONS  = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];
const PAYMENT_OPTIONS = ['unpaid', 'paid', 'refunded'];

export default function AdminOrders() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail,  setDetail]  = useState(null);

  const load = async () => {
    setLoading(true);
    const res = await orderApi.getAll();
    setOrders(res.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Deep-link support: /orders/:id opens the detail modal directly
  useEffect(() => {
    if (!id) return;
    orderApi.getById(id)
      .then((res) => setDetail(res.data))
      .catch(() => toast.error('Order not found'));
  }, [id]);

  const closeDetail = () => {
    setDetail(null);
    if (id) navigate('/orders');
  };

  const handleRefund = async (order) => {
    if (order.paymentStatus !== 'paid') return toast.error('Only paid orders can be refunded');
    if (!window.confirm(`Issue full refund of ₹${order.total.toFixed(2)} for ${order.orderNumber}?`)) return;
    try {
      await paymentApi.refund(order.id);
      toast.success('Refund initiated via Razorpay');
      load();
      closeDetail();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Refund failed');
    }
  };

  const updateStatus = async (orderId, status, paymentStatus) => {
    try {
      await orderApi.updateStatus(orderId, { status, paymentStatus });
      toast.success('Order updated');
      load();
      if (detail?.id === orderId) setDetail((p) => ({ ...p, status, paymentStatus }));
    } catch {
      toast.error('Failed to update order');
    }
  };

  return (
    <div className="admin-page">
      <h1 className="admin-page-title">Orders</h1>

      {loading ? <div className="spinner"/> : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order #</th><th>Customer</th><th>Items</th><th>Total</th>
                <th>Status</th><th>Payment</th><th>Date</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td>
                    <button className="link-btn" onClick={() => setDetail(o)}>{o.orderNumber}</button>
                  </td>
                  <td>{o.userEmail}</td>
                  <td>{o.items.length}</td>
                  <td>₹{o.total.toFixed(2)}</td>
                  <td>
                    <select value={o.status}
                      onChange={(e) => updateStatus(o.id, e.target.value, o.paymentStatus)}
                      className={`status-select status-${o.status}`}>
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td>
                    <select value={o.paymentStatus}
                      onChange={(e) => updateStatus(o.id, o.status, e.target.value)}
                      className="status-select">
                      {PAYMENT_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td>{new Date(o.createdAt).toLocaleDateString('en-IN')}</td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={() => setDetail(o)}>View</button>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={8} className="empty-row">No orders yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Order Detail Drawer */}
      {detail && (
        <div className="modal-overlay" onClick={closeDetail}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <h2>{detail.orderNumber}</h2>
            <p className="meta-text">{detail.userEmail} · {new Date(detail.createdAt).toLocaleString('en-IN')}</p>

            <div className="admin-table-wrapper" style={{ marginTop: 16 }}>
              <table className="admin-table">
                <thead>
                  <tr><th>Product</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr>
                </thead>
                <tbody>
                  {detail.items.map((item, i) => (
                    <tr key={i}>
                      <td>{item.name}</td>
                      <td>{item.quantity} {item.unit}</td>
                      <td>₹{item.price.toFixed(2)}</td>
                      <td>₹{(item.price * item.quantity).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="order-totals" style={{ marginTop: 12 }}>
              <div><span>Subtotal</span><span>₹{detail.subtotal.toFixed(2)}</span></div>
              <div><span>Shipping</span><span>₹{detail.shippingCost.toFixed(2)}</span></div>
              <div className="total"><span>Total</span><span>₹{detail.total.toFixed(2)}</span></div>
            </div>

            <div className="order-address" style={{ marginTop: 16 }}>
              <h4>Shipping Address</h4>
              <p>{detail.shippingAddress.name}</p>
              <p>{detail.shippingAddress.line1}{detail.shippingAddress.line2 && `, ${detail.shippingAddress.line2}`}</p>
              <p>{detail.shippingAddress.city}, {detail.shippingAddress.state} – {detail.shippingAddress.pincode}</p>
              <p>{detail.shippingAddress.phone}</p>
            </div>

            {detail.notes && <div style={{ marginTop: 12 }}><strong>Notes:</strong> {detail.notes}</div>}

            {detail.razorpayPaymentId && (
              <div style={{ marginTop: 12, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                Razorpay Payment ID: <strong>{detail.razorpayPaymentId}</strong>
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: 20 }}>
              <button className="btn btn-ghost" onClick={closeDetail}>Close</button>
              {detail.paymentStatus === 'paid' && (
                <button className="btn btn-danger" onClick={() => handleRefund(detail)}>
                  Issue Refund
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
